import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { AppContext } from 'Context/AppContext';
import {
  ADD_CATEGORY, COUNT_INFO_TEXT, COUNT_INFO_TITLE,
  CREATE_TRANSFORMATION_FIELD,
  DATA_CONFIGURATION, QUANTITY_INFO_TEXT, QUANTITY_INFO_TITLE,
  QUANTITY_PER_SELECTION, QUANTITY_PER_SELECTION_INFO_TEXT, QUANTITY_PER_SELECTION_INFO_TITLE,
  TRANSFORMATION,
} from "../ISRConstants";
import { DeleteIcon, NavRightIcon } from "assests/GmsIcons";
import Card from 'Components/DesignSystem/Card/Card';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import DsTooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import CategoryImport from "../Pages/DefineModel/Components/CategoryImport";
import {
  Data,
  TCategory,
  TConnector,
  TField,
  TProductCategory,
  TSubmodel,
  TTransformation,
  TIndexState, TUpsertISRProjectDataModelsInput,
} from "../Pages/DefineModel/model";
import {
  ENTER,
  MAX,
  MIN,
  COUNT,
  QUANTITY,
} from 'Pages/GMS/ISR/ISRConstants';
import { useLocation } from "react-router";
import { toast } from 'Components/DesignSystem/Toast/useToast';

const GUIDE_LOCKED_MESSAGE = 'Available with Configure';

type TCategoryConfigurations = {
  categoryTypeList: string[];
  categoryData: Data[];
  selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
  updateFieldDataConfiguration: (updatedConfig: TProductCategory) => void;
  setSubSelectedItem: React.Dispatch<
    React.SetStateAction<TTransformation | null>
  >;
  indexInfo: TIndexState;
  setIndex?: React.Dispatch<React.SetStateAction<TIndexState>>;
  userProjectDataModels: TUpsertISRProjectDataModelsInput;
  setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
};

const CategoryConfigurations = ({
  categoryTypeList,
  categoryData,

  selectedItem,
  updateFieldDataConfiguration,
  setSubSelectedItem,
  indexInfo,
  setIndex,
  userProjectDataModels,
  setUserProjectDataModels
}: TCategoryConfigurations) => {
  const location = useLocation();
  const [initialProductCategory, setInitialProductCategory] = useState<TProductCategory>({
    type: "",
    id: null,
    data: {
      compatioScore: false,
      type: "",
      name: "",
      minCount: 0,
      maxCount: 1,
      minQuantity: 0,
      maxQuantity: 1,
      minQuantityPerSelection: 1,
      maxQuantityPerSelection: 1,
      belongsToDataModel: false,
      categories: { type: "", data: [] },
      transformations: [],
      required: false
    },
  });

  const isGuide = Boolean(location.pathname.includes('/guide/'));

  const { enableConfigure } = useContext(AppContext);

  const isConfigureDisabled = isGuide && !enableConfigure;

  const [countRangeError, setCountRangeError] = useState<string>('');
  const [quantityRangeError, setQuantityRangeError] = useState<string>('');
  const [quantityPerSelectionRangeError, setQuantityPerSelectionRangeError] = useState<string>('');

  const [categoryConfiguration,setCategoryConfiguration,] = useState<TProductCategory | null>(initialProductCategory);
  const [selectedItems, setSelectedItems] = useState<TCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<{
    type: string;
    data: TCategory[];
  } | null>(null);
  const [selectedCategoriesMap, setSelectedCategoriesMap] = useState<{
    [key: string]: TCategory[];
  }>({});
  const allSelectedCategories: TCategory[] = Object.values(
    selectedCategoriesMap
  ).flat();
  const [showCategoryMenu, setShowCategoryMenu] = useState<boolean>(false);
  const openCategoryMenu = () => setShowCategoryMenu(true);

  useEffect(() => {
    const category = selectedItem as TProductCategory;
    if (category) {
      setCategoryConfiguration(category);
      setSelectedCategoriesMap({
        [category?.data?.categories?.type]: category?.data?.categories?.data,
      });
      setSelectedCategory(category?.data?.categories ?? { type: "", data: [] });
      const data = category?.data;
      if (data) {
        const minC = data.minCount ?? 0;
        const maxC = data.maxCount ?? 0;
        setCountRangeError(minC > maxC ? 'Minimum value cannot be greater than the maximum.' : '');
        const minQ = data.minQuantity ?? 0;
        const maxQ = data.maxQuantity ?? 0;
        setQuantityRangeError(minQ > maxQ ? 'Minimum value cannot be greater than the maximum.' : '');
        const minQPS = data.minQuantityPerSelection ?? 1;
        const maxQPS = data.maxQuantityPerSelection ?? 1;
        setQuantityPerSelectionRangeError(minQPS > maxQPS ? 'Minimum value cannot be greater than the maximum.' : '');
      }
    } else {
      setCategoryConfiguration(initialProductCategory);
    }
  }, [initialProductCategory]);

  useEffect(() => {
    setInitialProductCategory(selectedItem as TProductCategory);
  }, [selectedItem]);


  const removeDataModelFieldMultiFilters = (deletedCategoryId?: string) => {
    setUserProjectDataModels((dataModels) => {
      const newDataModels = {...dataModels};
      const dataModelToUpdate = newDataModels.project.dataModels[indexInfo?.dataModelIndex ?? -1];
      if (dataModelToUpdate) {
        dataModelToUpdate.data.fields.forEach((e) => {
          const filters = e.dataConfiguration.multiFilter?.filters;
          if (e.dataConfiguration.multiFilter && filters && filters.length > 0) {
            // Only drop filter rows bound to the removed category; leave filters
            // referencing the sub-model's surviving categories untouched.
            e.dataConfiguration.multiFilter.filters = deletedCategoryId
              ? filters.filter((f) => f.categoryId !== deletedCategoryId)
              : [];
          }
        });
      }
      return newDataModels;
    });
  }

  // US 13052 / AC #2: resolve a category's CURRENT name (fresh Neo4j list) by its
  // stable id. The name stored on the model is not auto-updated when the category
  // is renamed in the Category module, so a mismatch means an unresolved rename.
  const getCurrentCategoryName = (categoryId?: string): string | undefined => {
    if (!categoryId) return undefined;
    for (const categoryType of categoryData ?? []) {
      const match = categoryType?.category?.find((cat) => cat?.id === categoryId);
      if (match?.name) return match.name;
    }
    return undefined;
  };

  // US 13052: resolve the CURRENT category-type name from the fresh Neo4j list.
  // The model stores the type NAME (no id), which goes stale when the category
  // type is renamed — so locate the type that owns the selected category (by its
  // stable category id) and show that type's live name. Falls back to the stored
  // name while no category is selected or the category no longer exists.
  const getCurrentCategoryTypeName = (): string | undefined => {
    const storedTypeName = categoryConfiguration?.data?.categories?.type;
    const selectedCategoryId = categoryConfiguration?.data?.categories?.data?.[0]?.id;
    if (selectedCategoryId) {
      const owningType = (categoryData ?? []).find((categoryType) =>
        categoryType?.category?.some((cat) => cat?.id === selectedCategoryId));
      if (owningType?.name) return owningType.name;
    }
    return storedTypeName;
  };

  // US 13052 / AC #3: true when any filter or rule in the model still references
  // the given category id — switching away from it would break them.
  const hasCategoryDependencies = (categoryId?: string): boolean => {
    if (!categoryId) return false;
    const ruleReferences = (rules?: { targetCategory?: string | null }[]): boolean =>
      (rules ?? []).some((rule) => rule?.targetCategory === categoryId);
    const filterReferences = (field?: TField): boolean => {
      const dataConfiguration = field?.dataConfiguration;
      return (
        dataConfiguration?.filter?.categoryId === categoryId ||
        (dataConfiguration?.filters ?? []).some((f) => f?.categoryId === categoryId) ||
        (dataConfiguration?.multiFilter?.filters ?? []).some((f) => f?.categoryId === categoryId)
      );
    };
    const dataModels = userProjectDataModels?.project?.dataModels ?? [];
    return dataModels.some((dataModel) => {
      const fields = dataModel?.data?.fields ?? [];
      if (fields.some((field) => filterReferences(field) || ruleReferences(field?.rules))) {
        return true;
      }
      const productCategories = dataModel?.data?.productCategories ?? [];
      return productCategories.some((productCategory) =>
        (productCategory?.data?.transformations ?? []).some((transformation) =>
          ruleReferences(transformation?.rules)
        )
      );
    });
  };

  const handleDeleteField = (index: number) => {
    removeDataModelFieldMultiFilters(allSelectedCategories[index]?.id);
    const updatedCategories = [...allSelectedCategories];
    updatedCategories.splice(index, 1);

    setSelectedCategory((prevSelectedCategory) => {
      if (!prevSelectedCategory) return null;
      return {
        ...prevSelectedCategory,
        data: updatedCategories,
      };
    });

    setCategoryConfiguration((prevMap) => {
      if (!prevMap) return prevMap;
      const updatedData = {
        ...prevMap.data,
        categories: {
          type: selectedCategory?.type ?? "",
          data: updatedCategories,
        },
      };
      return {
        ...prevMap,
        data: updatedData,
      };
    });

    setSelectedCategoriesMap((prevMap) => ({
      ...prevMap,
      [selectedCategory?.type ?? ""]: updatedCategories,
    }));
  };

  const addNewTranformationField = () => {
    setCategoryConfiguration((prevMap) => {
      if (!prevMap) return prevMap;
      const newTranformation = { name: "", rules: [], type: TRANSFORMATION };
      const updatedData = {
        ...prevMap.data,
      };
      if (updatedData?.transformations) updatedData?.transformations?.push(newTranformation);
      else updatedData.transformations = [newTranformation];
      setTimeout(() => {
        if (setIndex)
          setIndex((prev) => ({
            ...prev,
            TransformationIndex: updatedData?.transformations?.length - 1,
          }));
        setSubSelectedItem(updatedData.transformations[updatedData?.transformations?.length - 1]);
      });
      const newState = {
        ...prevMap,
        data: updatedData,
      };
      return newState;
    });
  };

  useEffect(() => {
    if (allSelectedCategories.length === 0) return;
    if (categoryConfiguration) {
      if (!countRangeError && !quantityRangeError && !quantityPerSelectionRangeError) {
        updateFieldDataConfiguration(categoryConfiguration);
      }
    }
  }, [categoryConfiguration, countRangeError, quantityRangeError, quantityPerSelectionRangeError]);

  /*
   * The four data-configuration flags.
   *
   * These used to MUTATE `prevConfig` and return the same reference:
   *
   *     setCategoryConfiguration((prevConfig) => {
   *       prevConfig.data.compatioScore = check;
   *       return prevConfig;            // <- same object
   *     });
   *
   * `useState` bails out when `Object.is(prev, next)`, so returning the same object
   * registered no state change at all. Two things followed, and the second is the
   * expensive one:
   *
   *   - no re-render, so the DS `Toggle` never redrew. It is a `role="switch"` button
   *     whose visual comes from the `checked` PROP, so the switch simply did not move.
   *   - the effect keyed on `[categoryConfiguration, …]` never re-ran, so
   *     `updateFieldDataConfiguration` was never called and the flag never reached the
   *     model. The toggle was not just unresponsive; it did not save.
   *
   * This was NOT introduced by the design-system migration. `origin/gms-stage` has the
   * byte-identical mutation; it renders the legacy `ToggleButton`, a visually-hidden
   * `<input type="checkbox">` with a `peer-checked` sibling, so the BROWSER flipped the
   * control on click and React — never re-rendering — never reset it. The switch
   * animated and nothing was saved. The migration turned silent data loss into a
   * visible defect, which is why it was reported.
   *
   * The clone below is the shape every numeric handler in this file already uses
   * (`handleMinCount` and friends), which is also the evidence that this path is safe:
   * those go through the same effect and have always worked.
   */
  const handleCompatioScore = (check: boolean) => {
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      return { ...prevConfig, data: { ...prevConfig.data, compatioScore: check } };
    });
  };

  const handlePredictiveSort = (check: boolean) => {
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      return { ...prevConfig, data: { ...prevConfig.data, predictiveSort: check } };
    });
  };

  const handleBuildToLaunch = (check: boolean) => {
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      return { ...prevConfig, data: { ...prevConfig.data, buildToLaunch: check } };
    });
  };

  const handleAutoAddToCart = (check: boolean) => {
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      return { ...prevConfig, data: { ...prevConfig.data, autoAddToCart: check } };
    });
  };

    const clearVdaForCategoryFilters = () => {
    const categoryIds = categoryConfiguration?.data?.categories?.data?.map((cat) => cat.id) ?? [];
    if (categoryIds.length === 0) return;

    setUserProjectDataModels((dataModels) => {
      const dataModel = dataModels.project.dataModels[indexInfo?.dataModelIndex ?? -1];
      if (!dataModel) return dataModels;

      const currentSubModelId = dataModel.id;

      dataModel.data.fields.forEach((field: any) => {
        const filters = field.dataConfiguration?.multiFilter?.filters ?? [];
        filters.forEach((filter: any, idx: number) => {
          if (filter.vda && filter.subModelId === currentSubModelId && categoryIds.includes(filter.categoryId)) {
            filters[idx] = { ...filter, vda: false };
          }
        });
      });

      return { ...dataModels };
    });
  };

  // Check if any VDA field is using categories from this productCategory
    const hasVdaFieldUsingCategory = (): boolean => {
      const categoryIds = categoryConfiguration?.data?.categories?.data?.map((cat) => cat.id) ?? [];
      if (categoryIds.length === 0) return false;

      const dataModel = userProjectDataModels?.project?.dataModels?.[indexInfo?.dataModelIndex ?? -1];
      if (!dataModel) return false;

      return dataModel.data?.fields?.some((field) =>
        (field.dataConfiguration?.filters ?? []).some(
          (filter) => filter.vda === true && categoryIds.includes(filter.categoryId)
        )
      ) ?? false;
    };
   
    const handleMinCount = (value: number) => {
      const clampedValue = Math.max(0, value);
      const isRequired: boolean = categoryConfiguration?.data?.required !== undefined
        ? !!(categoryConfiguration?.data?.required) : false;
      if (isRequired && clampedValue < 1) {
        toast.warning('The required toggle is on so min value cannot be less than 1');
        return;
      }
      if (clampedValue > 1 && hasVdaFieldUsingCategory()) {
        toast.warning('Cannot set category min count greater than 1 when a VDA field is using this category. Disable VDA first to increase the count.');
        return;
      }
      const currentMaxCount = categoryConfiguration?.data?.maxCount ?? 0;
      if (clampedValue > currentMaxCount) {
        setCountRangeError('Minimum value cannot be greater than the maximum.');
      } else {
        setCountRangeError('');
      }
      setCategoryConfiguration((prevConfig) => {
        if (!prevConfig) return prevConfig;
        return { ...prevConfig, data: { ...prevConfig.data, minCount: clampedValue } };
      });
      if (clampedValue > 1) clearVdaForCategoryFilters();
    };

    const handleMaxCount = (value: number) => {
      const clampedValue = Math.max(0, value);
      if (clampedValue > 1 && hasVdaFieldUsingCategory()) {
        toast.warning('Cannot set category max count greater than 1 when a VDA field is using this category. Disable VDA first to increase the count.');
        return;
      }
      const currentMinCount = categoryConfiguration?.data?.minCount ?? 0;
      if (clampedValue < currentMinCount) {
        setCountRangeError('Minimum value cannot be greater than the maximum.');
      } else {
        setCountRangeError('');
      }
      setCategoryConfiguration((prevConfig) => {
        if (!prevConfig) return prevConfig;
        return { ...prevConfig, data: { ...prevConfig.data, maxCount: clampedValue } };
      });
      if (clampedValue > 1) clearVdaForCategoryFilters();
    };

  const handleMinQuantity = (value: number) => {
    const clampedValue = Math.max(0, value);
    const isRequired: boolean = categoryConfiguration?.data?.required !== undefined
        ? !!(categoryConfiguration?.data?.required) : false;
    if (isRequired && clampedValue < 1) {
      toast.warning('The required toggle is on so min value cannot be less than 1');
      return;
    }
    const currentMaxQuantity = categoryConfiguration?.data?.maxQuantity ?? 0;
    if (clampedValue > currentMaxQuantity) {
      setQuantityRangeError('Minimum value cannot be greater than the maximum.');
    } else {
      setQuantityRangeError('');
    }
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      return { ...prevConfig, data: { ...prevConfig.data, minQuantity: clampedValue } };
    });
  };

  const handleMaxQuantity = (value: number) => {
    const clampedValue = Math.max(0, value);
    const currentMinQuantity = categoryConfiguration?.data?.minQuantity ?? 0;
    if (clampedValue < currentMinQuantity) {
      setQuantityRangeError('Minimum value cannot be greater than the maximum.');
    } else {
      setQuantityRangeError('');
    }
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      return { ...prevConfig, data: { ...prevConfig.data, maxQuantity: clampedValue } };
    });
  };

  const handleMinQuantityPerSelection = (value: number) => {
    const clampedValue = Math.max(0, value);
    const isRequired: boolean = categoryConfiguration?.data?.required !== undefined
        ? !!(categoryConfiguration?.data?.required) : false;
    if (isRequired && clampedValue < 1) {
      toast.warning('The required toggle is on so min value cannot be less than 1');
      return;
    }
    const currentMaxQuantityPerSelection = categoryConfiguration?.data?.maxQuantityPerSelection ?? 1;
    if (clampedValue > currentMaxQuantityPerSelection) {
      setQuantityPerSelectionRangeError('Minimum value cannot be greater than the maximum.');
    } else {
      setQuantityPerSelectionRangeError('');
    }
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      return { ...prevConfig, data: { ...prevConfig.data, minQuantityPerSelection: clampedValue } };
    });
  };

  const handleMaxQuantityPerSelection = (value: number) => {
    const clampedValue = Math.max(0, value);
    const currentMinQuantityPerSelection = categoryConfiguration?.data?.minQuantityPerSelection ?? 1;
    if (clampedValue < currentMinQuantityPerSelection) {
      setQuantityPerSelectionRangeError('Minimum value cannot be greater than the maximum.');
    } else {
      setQuantityPerSelectionRangeError('');
    }
    setCategoryConfiguration((prevConfig) => {
      if (!prevConfig) return prevConfig;
      return { ...prevConfig, data: { ...prevConfig.data, maxQuantityPerSelection: clampedValue } };
    });
  };

  /**
   * One Count / Quantity / Quantity-per-Selection group.
   *
   * Written out three times before, identically apart from which pair of values it
   * bound to — 90 lines of markup for one shape.
   */
  const rangeGroup = (
    key: string,
    title: string,
    infoTitle: string,
    infoText: string,
    minValueShown: number,
    maxValueShown: number,
    onMin: (value: number) => void,
    onMax: (value: number) => void,
    error: string,
  ) => (
    <div id={`category-config-${key}`}>
      {/*
        InfoTooltip rather than FieldTitle's `infoMessage`, which is typed `string`:
        these hints are a bold heading over a body line, and flattening them into one
        sentence would lose that. InfoTooltip's `message` takes a ReactNode.
      */}
      <div className="tw-flex tw-items-center tw-gap-1">
        <Typography variant="sub-title-label" as="p" className="tw-text-ink-secondary">
          {title}
        </Typography>
        <InfoTooltip
          data-testid={`category-config-${key}-info`}
          placement="top"
          message={(
            <span className="tw-block">
              <span className="tw-block tw-font-semibold">{infoTitle}</span>
              <span className="tw-block">{infoText}</span>
            </span>
          )}
        />
      </div>
      <div className="tw-flex tw-gap-2 tw-mt-1">
        <TextField
          id={`category-config-${key}-min`}
          data-testid={`category-config-${key}-min`}
          label={MIN}
          type="number"
          minValue={0}
          error={!!error}
          value={String(minValueShown)}
          onChange={(value) => onMin(Number(value))}
          onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
          placeholder={ENTER}
          className="tw-w-20"
        />
        <TextField
          id={`category-config-${key}-max`}
          data-testid={`category-config-${key}-max`}
          label={MAX}
          type="number"
          minValue={0}
          error={!!error}
          value={String(maxValueShown)}
          onChange={(value) => onMax(Number(value))}
          onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
          placeholder={ENTER}
          className="tw-w-20"
        />
      </div>
      {error && (
        <Typography
          variant="caption-metadata"
          as="p"
          data-testid={`category-config-${key}-error`}
          className="tw-text-tone-danger-ink tw-mt-1 tw-max-w-[10.5rem] tw-break-words"
        >
          {error}
        </Typography>
      )}
    </div>
  );

  const flagToggle = (key: string, label: string, checked: boolean, onChange: (value: boolean) => void, disabled = false) => (
    <Toggle
      id={`category-config-${key}-toggle`}
      data-testid={`category-config-${key}-toggle`}
      label={label}
      labelPlacement="top"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
  );

  return (
    <Card id="category-config" data-testid="category-config">
      <div className="tw-flex tw-justify-between tw-items-center">
        <Typography variant="title-header" as="h3" className="tw-text-ink">
          {DATA_CONFIGURATION}
        </Typography>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
          <Button
            id="submodel-add-category-btn"
            data-testid="submodel-add-category-btn"
            variant="tertiary"
            size="sm"
            disabled={categoryConfiguration?.data?.categories?.data?.length !== 0}
            onClick={openCategoryMenu}
          >
            {ADD_CATEGORY}
          </Button>
          {categoryConfiguration?.data?.categories?.type ? (
            <Button
              id="submodel-category-type-btn"
              data-testid="submodel-category-type-btn"
              variant="tertiary"
              size="sm"
              icon={<NavRightIcon />}
              showIcon
              iconPosition="end"
              onClick={openCategoryMenu}
            >
              {getCurrentCategoryTypeName() ?? ""}
            </Button>
          ) : null}
        </div>
      </div>

      <div>
        {allSelectedCategories.map((category, index) => {
          const currentName = getCurrentCategoryName(category?.id);
          const isRenamed = Boolean(currentName && currentName !== category?.name);
          return (
            <div key={category.id}>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-rounded-ctl tw-bg-surface tw-box-border tw-w-[95%] tw-flex tw-flex-row tw-items-center tw-justify-start tw-py-2 tw-px-2.5 tw-mt-1 tw-gap-[10px] tw-border tw-border-edge">
                  <Typography variant="body-primary" className="tw-flex-1 tw-text-ink-secondary">
                    {category?.name ?? ""}
                  </Typography>
                </div>
                <Button
                  id={`category-delete-btn-${index}`}
                  data-testid={`components-categoryconfigurations-button-3-${index}`}
                  variant="subtle"
                  size="sm"
                  aria-label={`Delete ${category?.name ?? "category"}`}
                  icon={<DeleteIcon />}
                  showIcon
                  onClick={() => handleDeleteField(index)}
                >
                  {""}
                </Button>
              </div>
              {/* US 13052 / AC #2: inline warning while the stored category name is
                  stale after a rename; disappears once the user re-selects the
                  category (which stores the updated name). */}
              {isRenamed && (
                <Typography
                  variant="caption-metadata"
                  as="p"
                  id={`category-renamed-warning-${index}`}
                  className="tw-text-tone-danger-ink tw-mt-1 tw-break-words"
                >
                  {`Category renamed to ${currentName}. Please update.`}
                </Typography>
              )}
            </div>
          );
        })}
      </div>

      <div className="tw-flex tw-gap-x-6 tw-mt-4">
        {rangeGroup(
          'count', COUNT, COUNT_INFO_TITLE, COUNT_INFO_TEXT,
          categoryConfiguration?.data?.minCount ?? 0,
          categoryConfiguration?.data?.maxCount ?? 0,
          handleMinCount, handleMaxCount, countRangeError,
        )}
        {rangeGroup(
          'quantity', QUANTITY, QUANTITY_INFO_TITLE, QUANTITY_INFO_TEXT,
          categoryConfiguration?.data?.minQuantity ?? 0,
          categoryConfiguration?.data?.maxQuantity ?? 0,
          handleMinQuantity, handleMaxQuantity, quantityRangeError,
        )}
        {rangeGroup(
          'quantity-per-selection', QUANTITY_PER_SELECTION,
          QUANTITY_PER_SELECTION_INFO_TITLE, QUANTITY_PER_SELECTION_INFO_TEXT,
          categoryConfiguration?.data?.minQuantityPerSelection ?? 1,
          categoryConfiguration?.data?.maxQuantityPerSelection ?? 1,
          handleMinQuantityPerSelection, handleMaxQuantityPerSelection,
          quantityPerSelectionRangeError,
        )}
      </div>

      <div className="tw-flex tw-items-start tw-gap-16 tw-mt-6">
        {flagToggle('auto-add-to-build', 'Auto add to build', categoryConfiguration?.data?.autoAddToCart ?? false, handleAutoAddToCart)}
        {flagToggle('import-repository-rules', 'Import Repository Rules', categoryConfiguration?.data?.compatioScore ?? false, handleCompatioScore)}
        {/* `disabled` on the Tooltip, not a branch: the guide and non-guide arms were
            two near-identical copies of the same toggle before. */}
        <DsTooltip content={GUIDE_LOCKED_MESSAGE} disabled={!isConfigureDisabled} placement="top">
          <span>
            {flagToggle('predictive-sort', 'Predictive sort', categoryConfiguration?.data?.predictiveSort ?? false, handlePredictiveSort, isConfigureDisabled)}
          </span>
        </DsTooltip>
        {flagToggle('build-to-launch', 'Build to launch', categoryConfiguration?.data?.buildToLaunch ?? true, handleBuildToLaunch)}
      </div>

      {/*
        A Button, not a `<Link to="#" role="menuitem">`. It creates a transformation
        field; it never navigates, and "#" in the history is what a link to nowhere
        actually does. `role="menuitem"` was wrong too — there is no menu.
      */}
      <DsTooltip content={GUIDE_LOCKED_MESSAGE} disabled={!isConfigureDisabled} placement="right">
        <Button
          id="create-transformation-field-btn"
          data-testid="create-transformation-field-btn"
          variant="secondary"
          className="tw-mt-5"
          disabled={isConfigureDisabled}
          onClick={() => { addNewTranformationField(); }}
        >
          {CREATE_TRANSFORMATION_FIELD}
        </Button>
      </DsTooltip>

      {showCategoryMenu && (
        <CategoryImport
          showCategoryMenu={showCategoryMenu}
          setShowCategoryMenu={setShowCategoryMenu}
          categoryTypeList={categoryTypeList}
          categoryData={categoryData}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          setSelectedCategoriesMap={setSelectedCategoriesMap}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          setCategoryConfiguration={setCategoryConfiguration}
          removeDataModelFieldMultiFilters={removeDataModelFieldMultiFilters}
          hasCategoryDependencies={hasCategoryDependencies}
        />
      )}
    </Card>
  );
};

export default CategoryConfigurations;
